import * as R from 'ramda';
import { withFormik } from 'formik';
import { connect } from 'react-redux';
import React, { useEffect } from 'react';
import { createStructuredSelector } from 'reselect';
import { pure, branch, compose, renderComponent } from 'react-recompose';
// common
import { makeSelectInitialSplashScreen } from '../../common/selectors';
// components
import LoaderComponent from '../../components/loader';
import ImageSlider from '../../components/slider-component';
// helpers/constants
import * as G from '../../helpers';
import * as GC from '../../constants';
// ui
import { Flex } from '../../ui';
// auth settings
import {
  loginPageSettings,
  loginFieldSettings,
  defaultLoginInitFields,
  loginValidationSchemaObject,
} from './settings';
// feature auth
import AuthForm from './auth-form';
import { sendLogInRequest } from './actions';
import { makeSelectAuthData } from './selectors';
import stylingPanelHoc from './styling-panel-hoc';
import { createOptions, setDefaultSetting } from './helpers';
//////////////////////////////////////////////////

const ssoErrorMessagesMap = {
  sso_user_not_found: [
    'messages:sso:user-not-found',
    'Your Microsoft account was verified, but it is not linked to any Amous user. Please contact your administrator.',
  ],
  authentication_failed: [
    'messages:sso:authentication-failed',
    'Single Sign-On authentication failed. Please try again or contact your administrator.',
  ],
};

const showLoginHashError = () => {
  const errorCode = R.prop(GC.FIELD_ERROR, G.parseHashString());

  if (G.isNilOrEmpty(errorCode)) return;

  const [localeKey, defaultMessage] = R.propOr(
    ssoErrorMessagesMap.authentication_failed,
    errorCode,
    ssoErrorMessagesMap,
  );

  G.showToastrMessageSimple('error', G.getWindowLocale(localeKey, defaultMessage));

  const hashParams = new window.URLSearchParams(window.location.hash.replace(/^#/, ''));

  hashParams.delete(GC.FIELD_ERROR);

  const cleanHash = hashParams.toString();
  const cleanUrl = `${window.location.pathname}${window.location.search}${cleanHash ? `#${cleanHash}` : ''}`;

  window.history.replaceState({}, document.title, cleanUrl);
};

const Login = (props) => {
  const {
    handleSubmit,
    handleChange,
    isSubmitting,
    splashScreenSetting,
    handleGetInfoPanelRef,
  } = props;

  useEffect(showLoginHashError, []);

  const { title, action, subtitle, linkAction } = loginPageSettings;

  let options = [];

  if (G.isNotNil(R.path(['panels'], splashScreenSetting))) {
    const urlList = R.pick([
      'firstBackgroundImageUrl',
      'secondBackgroundImageUrl',
      'thirdBackgroundImageUrl',
    ], splashScreenSetting);

    options = createOptions(R.path(['panels'], splashScreenSetting), urlList);
  } else {
    options = setDefaultSetting(R.path(['firstBackgroundImageUrl'], splashScreenSetting));
  }

  return (
    <Flex
      m='0 auto'
      width='100vw'
      height='100vh'
      display='flex'
      overflow='auto'
      maxWidth={2800}
      alignItems='flex-start'
    >
      <ImageSlider
        slides={options}
        splashScreenSetting={splashScreenSetting}
        handleGetInfoPanelRef={handleGetInfoPanelRef}
        color={R.path(['panelColor'], splashScreenSetting)}
      />
      <AuthForm
        props={props}
        mbLinkTo={24}
        heightLogo={150}
        textAlign='center'
        widthSubmitBtn={1}
        subtitle={subtitle}
        withLegalInfo={true}
        fontSizeSubmitBtn={18}
        actionSubmitBtn={action}
        actionLinkTo={linkAction}
        displayInputsBlock='block'
        puddingSubmitBtn='8px 26px'
        handleSubmit={handleSubmit}
        handleChange={handleChange}
        isSubmitting={isSubmitting}
        fields={loginFieldSettings}
        imageSlidersOptions={options}
        splashScreenSetting={splashScreenSetting}
        pathLinkTo={GC.ROUTE_PATH_RESET_PASSWORD}
        title={R.or(R.path(['welcomeText'], splashScreenSetting), title)}
      />
    </Flex>
  );
};

const enhance = compose(
  stylingPanelHoc,
  withFormik({
    enableReinitialize: true,
    validationSchema: loginValidationSchemaObject,
    handleSubmit: (values, { props, setSubmitting }) => {
      const { sendLogInRequest } = props;

      setSubmitting(true);
      sendLogInRequest({...values, callback: () => setSubmitting(false) });
    },
    mapPropsToValues: ({ initialValues }) => G.setInitialFormikValues(
      defaultLoginInitFields,
      initialValues,
    ),
  }),
  branch(
    ({ auth, splashScreenSetting }) => R.or(
      R.not(R.path(['isLoaded'], splashScreenSetting)),
      R.path(['loggedIn'], auth)),
    renderComponent(LoaderComponent),
  ),
  pure,
);

const mapStateToProps = (state) => (createStructuredSelector({
  auth: makeSelectAuthData(state),
  splashScreenSetting: makeSelectInitialSplashScreen(state),
}));

export default connect(mapStateToProps, {
  sendLogInRequest,
})(enhance(Login));
